import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE } from "./content";

/**
 * Per-route SEO. One component, called once per page.
 * - title / description / canonical / robots
 * - Open Graph + Twitter card (image absolute)
 * - optional JSON-LD blocks (array of objects)
 */
// The static fallback tags in public/index.html (for crawlers without JS) are
// marked data-static-seo. Once React renders, Helmet owns <head>; remove the
// fallbacks so there is exactly one canonical / og:* / description per page.
function dropStaticSeoTags() {
  document.querySelectorAll('head [data-static-seo]').forEach((el) => el.remove());
}

export function Seo({ title, description, image, path, noindex = false, jsonLd = [], type = "website" }) {
  const ldList = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  useEffect(() => { dropStaticSeoTags(); }, []);
  const loc = useLocation();
  const p = path ?? loc.pathname;
  const url = SITE.url.replace(/\/$/, "") + (p === "/" ? "/" : p);
  const fullTitle = title ? `${title} — ${SITE.name}` : `${SITE.name} — ${SITE.tagline}`;
  const desc = description || SITE.defaultDescription;
  const img = SITE.url.replace(/\/$/, "") + (image || SITE.ogImage);
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large"} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      {SITE.twitter && <meta name="twitter:site" content={SITE.twitter} />}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {ldList.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}

export const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: SITE.url,
  logo: SITE.url + "/og/og-default.jpg",
  ...(SITE.email ? { email: SITE.email } : {}),
  ...(SITE.linkedin ? { sameAs: [SITE.linkedin] } : {}),
};

export function softwareLd(plans) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE.name,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: SITE.defaultDescription,
    url: SITE.url,
    offers: plans.map((p) => ({
      "@type": "Offer", name: p.name, price: p.basePrice, priceCurrency: "USD",
      // Starting price for up to includedSeats — actual price scales with
      // seats beyond that (basePrice + perSeat * extra seats), which
      // schema.org's UnitPriceSpecification has no clean way to express.
      priceSpecification: { "@type": "UnitPriceSpecification", price: p.basePrice, priceCurrency: "USD", billingDuration: "P1M" },
      url: SITE.url + "/pricing",
    })),
  };
}

export function faqLd(groups) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: groups.flatMap((g) => g.items).map((i) => ({
      "@type": "Question", name: i.q, acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  };
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: SITE.url + it.path })),
  };
}
